/**
 * Fit to Reference
 *
 * The placement rule of Bulk Creation: a new motif takes over the stored
 * placement of the reference listing (same center, same relative width) and
 * only its own aspect ratio. If the new motif then pokes out of the print
 * area by more than the shared tolerance, it is fitted inside.
 *
 * This deliberately differs from `carryOverPlacement`, which lets a design
 * the user intentionally overflowed keep that overflow: in a bulk run nobody
 * looked at the new motif on the product, so it must never be cropped. The
 * rule runs on stored rows only (the master row against the master print
 * area, each custom row against its own area, both in millimetres); derived
 * products are then carried over from the fitted master placement.
 *
 * The bulk job and the "will be scaled down" hint in the web table both call
 * this function so they always give the same answer.
 */

import { fitInside, overflows } from '../design/uniformRescale.js';
import type { RelativeDesignState } from '../design/placementTypes.js';
import type { PlacementArea } from '../placement/sizeTier.js';

export interface FitToReferenceResult {
  /** The placement for the new motif on this print area. */
  state: RelativeDesignState;
  /** The motif is rendered smaller than on the reference (relWidth reduced). */
  shrunk: boolean;
  /**
   * The motif overflowed and was fitted: scaled down and/or its center moved
   * inside the area. True whenever `shrunk` is true.
   */
  adjusted: boolean;
}

/**
 * Place a new motif with height/width ratio `newDesignRatio` where the
 * reference placement `referenceState` sits on `area`.
 *
 * @throws RangeError when `newDesignRatio` is not a positive finite number
 */
export function fitToReference(
  referenceState: RelativeDesignState,
  newDesignRatio: number,
  area: PlacementArea
): FitToReferenceResult {
  if (!Number.isFinite(newDesignRatio) || newDesignRatio <= 0) {
    throw new RangeError(
      `fitToReference: designRatio must be a positive finite number, got ${newDesignRatio}`
    );
  }

  const candidate: RelativeDesignState = {
    relCenterX: referenceState.relCenterX,
    relCenterY: referenceState.relCenterY,
    relWidth: referenceState.relWidth,
    designRatio: newDesignRatio,
  };

  if (!overflows(candidate, area)) {
    return { state: candidate, shrunk: false, adjusted: false };
  }

  const state = fitInside(candidate, area);
  return {
    state,
    shrunk: state.relWidth < candidate.relWidth,
    adjusted: true,
  };
}
